import React, { useState } from 'react'
import { Modal } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { RadioGroup, Radio } from 'components/RadioGroup'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { getPlatformDisplayName } from 'utils/platformDisplayNames'
import Styles from './DisconnectIntegrationModal.module.scss'
import { StackModalProps } from 'almanac-ui/Modal/Types'
import { Heading } from 'blaze-ui/Heading/Heading'

interface DisconnectIntegrationModalProps extends StackModalProps {
  integration: ExternalIntegration
  onDisconnect: (option: 'remove_channel' | 'keep_generating') => void
}

export const DisconnectIntegrationModal = ({ integration, onDisconnect, close }: DisconnectIntegrationModalProps) => {
  const [selectedOption, setSelectedOption] = useState<'remove_channel' | 'keep_generating'>('remove_channel')
  const integrationName = getPlatformDisplayName(integration.provider)

  const handleDisconnect = () => {
    onDisconnect(selectedOption)
    close()
  }

  return (
    <Modal.Root size="sm">
      <Modal.Header title={`Disconnect ${integrationName} integration?`} />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.consequences}>
            <Heading level={4}>If you disconnect:</Heading>
            <ul className={Styles.consequencesList}>
              <li>
                <Text>{integrationName} integration will be removed</Text>
              </li>
              <li>
                <Text>Scheduled posts will move to Drafts (reschedule before publishing)</Text>
              </li>
            </ul>
          </div>

          <RadioGroup value={selectedOption} onChange={(value) => setSelectedOption(value as typeof selectedOption)}>
            <div className={Styles.radioOptions}>
              <Radio value="remove_channel" className={Styles.radioOption} classNames={{ selected: Styles.selected }}>
                <div className={Styles.radioContent}>
                  <Heading level={5}>Remove channel</Heading>
                  <ul className={Styles.optionList}>
                    <li>
                      <Text variant="secondary">
                        {integrationName} will be removed from your Content Plan and related content
                      </Text>
                    </li>
                    <li>
                      <Text variant="secondary">No new content will generate</Text>
                    </li>
                  </ul>
                </div>
              </Radio>
              <Radio value="keep_generating" className={Styles.radioOption} classNames={{ selected: Styles.selected }}>
                <div className={Styles.radioContent}>
                  <Heading level={5}>Keep generating content</Heading>
                  <ul className={Styles.optionList}>
                    <li>
                      <Text variant="secondary">{integrationName} content will continue in your Content Plan</Text>
                    </li>
                    <li>
                      <Text variant="secondary">Connection will be removed</Text>
                    </li>
                  </ul>
                </div>
              </Radio>
            </div>
          </RadioGroup>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={close}>
            Keep Current
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button size="lg" variant="primary" onPress={handleDisconnect}>
            Disconnect with Selected Option
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
